import { Link, useParams, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ApiError, getCatalogProducts, getCategoryByPath, type ProductListQuery } from "@/lib/api-client";
import { parseListingSearchParams } from "@/lib/listing-query";
import { ListingControls } from "@/components/product/ListingControls";
import { LoadingBlock, ErrorBlock } from "@/components/product/QueryFeedback";
import { NotFoundPage } from "@/pages/shop/NotFoundPage";

/**
 * Équivalent de apps/web/app/(shop)/catalogue/[[...slug]]/page.tsx (catch-all
 * optionnel Next.js `[[...slug]]`). Deux entrées de route pointent ici (cf.
 * router.tsx) : `/catalogue` (index, splat absent) et `/catalogue/*` (splat =
 * segments restants, ex. "bijoux-montres/montres") — reproduit ici en
 * découpant `params["*"]` sur `/`, exactement comme l'original découpait
 * `slug: string[]` (déjà un tableau de segments côté Next.js catch-all).
 *
 * `robots`/repli SEO (page vide non indexée) volontairement absents — hors
 * périmètre de cet atelier (SEO/prérendu séparé), cf. description de tâche.
 */
export function CataloguePage() {
  const params = useParams<{ "*": string }>();
  const [searchParams] = useSearchParams();
  const slugSegments = (params["*"] ?? "").split("/").filter(Boolean);
  const filters = parseListingSearchParams(searchParams);

  if (slugSegments.length === 0) {
    return <AllCatalogueView filters={filters} />;
  }

  return <CategoryView path={slugSegments.join("/")} filters={filters} />;
}

type ListingFilters = Omit<ProductListQuery, "category" | "cursor">;

function AllCatalogueView({ filters }: { filters: ListingFilters }) {
  const { data, isLoading, isError } = useQuery({
    queryKey: ["catalog", "products", filters],
    queryFn: () => getCatalogProducts(filters),
  });

  if (isLoading) return <LoadingBlock />;
  if (isError || !data) return <ErrorBlock />;

  const hasResults = data.items.length > 0;
  const total = data.facets.availability.inStock + data.facets.availability.outOfStock;

  return (
    <div className="container-vvs py-4">
      {/* D · Fil d'Ariane simplifié : pas de catégorie précisée */}
      <nav aria-label="Fil d'Ariane" className="mb-3 flex flex-wrap gap-1 font-body text-xs text-muted">
        <Link to="/" className="hover:text-navy">
          Accueil
        </Link>
        <span aria-hidden="true">/</span>
        <span className="text-ink">Catalogue</span>
      </nav>

      {/* 1 · En-tête de listing */}
      <header className="mb-4">
        <h1 className="font-display text-2xl font-bold text-navy sm:text-3xl">
          {filters.onSale ? "Produits en promotion" : "Tout le catalogue"}
        </h1>
        <p className="mt-2 max-w-prose font-body text-sm text-muted">
          {filters.onSale
            ? "Tous les produits actuellement en promotion chez VVS."
            : "Toutes les catégories, toutes les marques : parcourez l'ensemble des produits VVS."}
        </p>
      </header>

      {hasResults ? (
        <ListingControls
          facets={data.facets}
          total={total}
          initialItems={data.items}
          initialNextCursor={data.nextCursor}
          query={filters}
        />
      ) : (
        <div className="py-16 text-center">
          <p className="font-body text-base text-ink">
            Aucun produit ne correspond à votre sélection pour le moment.
          </p>
          <p className="mt-2 font-body text-sm text-muted">
            Essayez de retirer un filtre, ou explorez{" "}
            <Link to="/" className="text-navy underline">
              nos autres rayons
            </Link>
            .
          </p>
        </div>
      )}
    </div>
  );
}

function CategoryView({ path, filters }: { path: string; filters: ListingFilters }) {
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ["catalog", "category", path, filters],
    queryFn: () => getCategoryByPath(path, filters),
  });

  if (isLoading) return <LoadingBlock />;
  if (error instanceof ApiError && error.status === 404) return <NotFoundPage />;
  if (isError || !data) return <ErrorBlock />;

  const { category, products } = data;
  const hasResults = products.items.length > 0;
  const total = products.facets.availability.inStock + products.facets.availability.outOfStock;
  const parentCrumb = category.breadcrumb[category.breadcrumb.length - 2];

  return (
    <div className="container-vvs py-4">
      {/* D · Fil d'Ariane */}
      <nav aria-label="Fil d'Ariane" className="mb-3 flex flex-wrap gap-1 font-body text-xs text-muted">
        {category.breadcrumb.map((crumb, i) => (
          <span key={crumb.href} className="flex items-center gap-1">
            {i > 0 ? <span aria-hidden="true">/</span> : null}
            {i === category.breadcrumb.length - 1 ? (
              <span className="text-ink">{crumb.label}</span>
            ) : (
              <Link to={crumb.href} className="hover:text-navy">
                {crumb.label}
              </Link>
            )}
          </span>
        ))}
      </nav>

      {/* 1 · En-tête de listing */}
      <header className="mb-4">
        <h1 className="font-display text-2xl font-bold text-navy sm:text-3xl">{category.name}</h1>
        {category.descriptionShort ? (
          <p className="mt-2 max-w-prose font-body text-sm text-muted">{category.descriptionShort}</p>
        ) : null}
      </header>

      {/* 2 · Sous-catégories */}
      {category.children.length > 0 ? (
        <div className="mb-4 flex gap-2 overflow-x-auto pb-2">
          {category.children.map((child) => (
            <Link
              key={child.id}
              to={`/catalogue/${path}/${child.slug}`}
              className="shrink-0 whitespace-nowrap rounded-full border border-line bg-white px-4 py-2 font-body text-sm text-ink hover:border-navy"
            >
              {child.name}
            </Link>
          ))}
        </div>
      ) : null}

      {hasResults ? (
        <ListingControls
          facets={products.facets}
          total={total}
          initialItems={products.items}
          initialNextCursor={products.nextCursor}
          query={{ category: path, ...filters }}
        />
      ) : (
        <div className="py-16 text-center">
          <p className="font-body text-base text-ink">
            Aucun produit ne correspond à votre sélection pour le moment.
          </p>
          <p className="mt-2 font-body text-sm text-muted">
            Essayez de retirer un filtre, ou explorez{" "}
            {parentCrumb ? (
              <Link to={parentCrumb.href} className="text-navy underline">
                {parentCrumb.label}
              </Link>
            ) : (
              <Link to="/" className="text-navy underline">
                nos autres rayons
              </Link>
            )}
            .
          </p>
        </div>
      )}

      {/* 6 · Texte SEO de catégorie, replié */}
      {category.descriptionSeo ? (
        <details className="mt-10 border-t border-line pt-4">
          <summary className="cursor-pointer font-body text-sm font-medium text-navy">
            En savoir plus sur {category.name}
          </summary>
          <p className="mt-3 max-w-prose font-body text-sm text-muted">{category.descriptionSeo}</p>
        </details>
      ) : null}
    </div>
  );
}
